import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSocialStore } from '@/stores/social';
import { useHiStore } from '@/stores/hi';
import { mediaUrl } from '@/hooks/useMediaUrl';
import { api } from '@/lib/api';
import type { ActivityLogEntry } from '@shared/types';

function OnlineFriendsSidebar() {
  const { onlineFriends, fetchOnlineFriends } = useSocialStore();

  useEffect(() => {
    fetchOnlineFriends();
    const interval = setInterval(fetchOnlineFriends, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="card">
      <div className="card-header flex items-center gap-1.5">
        <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
        Online Now
      </div>
      <div className="p-2">
        {onlineFriends.length === 0 ? (
          <p className="text-xs text-sand-500 px-1 py-2">It's quiet out there ∿</p>
        ) : (
          <div className="space-y-0.5">
            {onlineFriends.map((friend) => (
              <Link
                key={friend.id}
                to={`/profile/${friend.username}`}
                className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-sand-50 transition-colors"
              >
                <div className="relative shrink-0">
                  <img src={mediaUrl(friend.avatar_key)} alt="" className="w-6 h-6 rounded object-cover" />
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                </div>
                <span className="text-xs font-semibold text-sand-700 truncate">{friend.display_name}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BirthdaySidebar() {
  const { birthdays, fetchBirthdays } = useSocialStore();

  useEffect(() => {
    fetchBirthdays();
  }, []);

  if (birthdays.length === 0) return null;

  const formatBirthday = (mmdd: string) => {
    const [month, day] = mmdd.split('-').map(Number);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const today = new Date();
    const isToday = today.getMonth() + 1 === month && today.getDate() === day;
    return isToday ? 'Today!' : `${months[month - 1]} ${day}`;
  };

  const isBirthdayToday = (mmdd: string) => {
    const [month, day] = mmdd.split('-').map(Number);
    const today = new Date();
    return today.getMonth() + 1 === month && today.getDate() === day;
  };

  return (
    <div className="card">
      <div className="card-header">&#127874; Birthdays</div>
      <div className="p-2 space-y-1">
        {birthdays.map((friend) => (
          <div key={friend.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-sand-50 transition-colors">
            <Link to={`/profile/${friend.username}`} className="shrink-0">
              <img src={mediaUrl(friend.avatar_key)} alt="" className="w-6 h-6 rounded object-cover" />
            </Link>
            <div className="min-w-0 flex-1">
              <Link to={`/profile/${friend.username}`} className="text-xs font-semibold text-sand-700 truncate block">
                {friend.display_name}
              </Link>
              <span className="text-[10px] text-sand-500">
                &#127874; {formatBirthday(friend.birthday)}
              </span>
              {isBirthdayToday(friend.birthday) && (
                <Link
                  to={`/profile/${friend.username}?birthday=1`}
                  className="block text-[10px] text-vibe-500 hover:text-vibe-600 font-semibold mt-0.5"
                >
                  Write on their wall
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PeopleYouMayKnow() {
  const { suggestions, fetchSuggestions } = useSocialStore();
  const [sent, setSent] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchSuggestions();
  }, []);

  if (suggestions.length === 0) return null;

  const sendRequest = async (userId: string) => {
    try {
      await api.post('/connections/request', { user_id: userId });
      setSent((prev) => new Set(prev).add(userId));
    } catch {
      // ignore
    }
  };

  return (
    <div className="card">
      <div className="card-header">&#128101; You Might Know</div>
      <div className="p-2 space-y-1">
        {suggestions.slice(0, 5).map((person) => (
          <div key={person.id} className="flex items-center gap-2 px-1.5 py-1.5">
            <Link to={`/profile/${person.username}`} className="shrink-0">
              <img src={mediaUrl(person.avatar_key)} alt="" className="w-8 h-8 rounded object-cover" />
            </Link>
            <div className="min-w-0 flex-1">
              <Link to={`/profile/${person.username}`} className="link text-xs font-bold block truncate">
                {person.display_name}
              </Link>
              <span className="text-[10px] text-sand-500">
                {person.mutual_count} mutual friend{person.mutual_count !== 1 ? 's' : ''}
              </span>
            </div>
            {sent.has(person.id) ? (
              <span className="text-[10px] text-sand-500 shrink-0">Sent &#10003;</span>
            ) : (
              <button
                onClick={() => sendRequest(person.id)}
                className="text-[10px] font-bold text-vibe-500 hover:text-vibe-600 shrink-0"
              >
                + Add
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function HisSidebar() {
  const { his, fetchHis, sendHi, dismissHi } = useHiStore();
  const [hiBackSent, setHiBackSent] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchHis();
    const interval = setInterval(fetchHis, 30000);
    return () => clearInterval(interval);
  }, []);

  if (his.length === 0) return null;

  const handleHiBack = async (senderId: string, hiId: string) => {
    try {
      await sendHi(senderId);
      setHiBackSent((prev) => new Set(prev).add(hiId));
      // Dismiss the original Hi after sending back
      setTimeout(() => dismissHi(hiId), 1500);
    } catch {}
  };

  return (
    <div className="card">
      <div className="card-header">&#128075; Your Hi's</div>
      <div className="p-2 space-y-1">
        {his.map((hi) => (
          <div key={hi.id} className="flex items-center gap-2 px-1.5 py-1.5">
            <Link to={`/profile/${hi.sender_username}`} className="shrink-0">
              <img src={mediaUrl(hi.sender_avatar_key)} alt="" className="w-8 h-8 rounded object-cover" />
            </Link>
            <div className="min-w-0 flex-1">
              <Link to={`/profile/${hi.sender_username}`} className="link text-xs font-bold block truncate">
                {hi.sender_display_name}
              </Link>
              <span className="text-[10px] text-sand-500">said Hi!</span>
            </div>
            {hiBackSent.has(hi.id) ? (
              <span className="text-[10px] text-sand-500 shrink-0">Sent! &#128075;</span>
            ) : (
              <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => handleHiBack(hi.sender_id, hi.id)}
                  className="text-[10px] font-bold text-amber-600 hover:text-amber-700"
                >
                  Hi back
                </button>
                <button
                  onClick={() => dismissHi(hi.id)}
                  className="text-[10px] text-sand-400 hover:text-sand-600"
                >
                  &#10005;
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityTicker() {
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);

  const fetchActivity = async () => {
    try {
      const data = await api.get<ActivityLogEntry[]>('/activity/friends?limit=10');
      setActivities(data);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    fetchActivity();
    const interval = setInterval(fetchActivity, 30000);
    return () => clearInterval(interval);
  }, []);

  if (activities.length === 0) return null;

  const describeActivity = (a: ActivityLogEntry) => {
    const name = a.user_display_name;
    const target = a.target_display_name;
    switch (a.type) {
      case 'new_friend':
        return (
          <>
            <Link to={`/profile/${a.user_username}`} className="link font-bold">{name}</Link>
            {' and '}
            {target && a.target_username ? (
              <Link to={`/profile/${a.target_username}`} className="link font-bold">{target}</Link>
            ) : 'someone'}
            {' are now friends'}
          </>
        );
      case 'like':
        return (
          <>
            <Link to={`/profile/${a.user_username}`} className="link font-bold">{name}</Link>
            {' liked a post'}
          </>
        );
      case 'comment':
        return (
          <>
            <Link to={`/profile/${a.user_username}`} className="link font-bold">{name}</Link>
            {' commented on a post'}
          </>
        );
      case 'new_post':
        return (
          <>
            <Link to={`/profile/${a.user_username}`} className="link font-bold">{name}</Link>
            {' shared a new post'}
          </>
        );
      default:
        return (
          <>
            <Link to={`/profile/${a.user_username}`} className="link font-bold">{name}</Link>
            {` ${a.type.replace(/_/g, ' ')}`}
          </>
        );
    }
  };

  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const then = new Date(dateStr + 'Z');
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
  };

  return (
    <div className="card">
      <div className="card-header">&#9889; Ticker</div>
      <div className="p-2 space-y-1.5">
        {activities.map((a) => (
          <div key={a.id} className="text-[10px] text-sand-600 leading-tight">
            {describeActivity(a)}
            <span className="text-sand-400 ml-1">{timeAgo(a.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RightSidebar() {
  return (
    <div className="hidden lg:block w-52 shrink-0 space-y-3">
      <HisSidebar />
      <OnlineFriendsSidebar />
      <BirthdaySidebar />
      <PeopleYouMayKnow />
      <div className="card">
        <div className="card-header">&#128197; What's Happening</div>
        <div className="p-2 text-xs text-sand-500">
          <a href="/events" className="link">See what's happening &#8594;</a>
        </div>
      </div>
      <ActivityTicker />
    </div>
  );
}
